import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchRouteAssets } from './api/routingApi';
import { PlannerState, RouteAssetsReport } from './types';

/** Long enough that dragging a waypoint never fires a scan mid-gesture. */
const DEBOUNCE_MS = 900;

export const DEFAULT_SERVICE_RADIUS_M = 250;

export interface UseRouteAssets {
  enabled: boolean;
  setEnabled: (on: boolean) => void;
  radiusM: number;
  setRadiusM: (metres: number) => void;
  report: RouteAssetsReport | null;
  loading: boolean;
  error: string | null;
  /** The report describes a route or radius that is no longer the current one. */
  stale: boolean;
  /** When the held report was fetched. */
  capturedAt: string | null;
  refresh: () => void;
}

/**
 * Infrastructure on and beside the selected route.
 *
 * Opt-in and debounced harder than corridor analysis: this one leaves the
 * platform for OpenStreetMap, so it should never fire speculatively while an
 * operator is still moving waypoints around.
 *
 * A fetched report is kept until a newer one replaces it — through hiding the
 * panel, through editing the route, and through a failed request. A scan costs
 * a third-party round trip, and throwing the last good answer away the moment
 * the operator nudges a waypoint means they lose a list they were reading from
 * and have to wait to get it back. Anything no longer describing the current
 * route is reported as `stale` instead.
 */
export function useRouteAssets(state: PlannerState): UseRouteAssets {
  const [enabled, setEnabled] = useState(false);
  const [radiusM, setRadiusM] = useState(DEFAULT_SERVICE_RADIUS_M);
  const [report, setReport] = useState<RouteAssetsReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [fetchedKey, setFetchedKey] = useState<string | null>(null);
  const [capturedAt, setCapturedAt] = useState<string | null>(null);
  const fetched = useRef<{ key: string; nonce: number } | null>(null);

  const primary = state.routes[state.primaryIndex] || null;

  // Geometry identity changes on every re-render of the route list; key on the
  // shape itself so a re-render never re-queries OSM.
  const geometryKey = useMemo(() => {
    const coords = (primary?.geometry?.coordinates || []) as number[][];
    if (coords.length < 2) return '';
    const first = coords[0];
    const last = coords[coords.length - 1];
    return `${coords.length}:${first[0].toFixed(4)},${first[1].toFixed(4)}`
      + `:${last[0].toFixed(4)},${last[1].toFixed(4)}`;
  }, [primary]);

  const requestKey = `${geometryKey}|${radiusM}`;

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;
    const coordinates = (primary?.geometry?.coordinates || []) as number[][];
    if (coordinates.length < 2) return undefined;
    // Already answered this exact question; re-asking OSM would return the same
    // list at the cost of a round trip.
    if (fetched.current?.key === requestKey && fetched.current.nonce === nonce) {
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const next = await fetchRouteAssets(
          { coordinates, service_radius_m: radiusM },
          controller.signal,
        );
        if (cancelled) return;
        setReport(next);
        setFetchedKey(requestKey);
        setCapturedAt(new Date().toISOString());
        fetched.current = { key: requestKey, nonce };
      } catch (e: any) {
        if (cancelled || axios.isCancel(e) || e?.code === 'ERR_CANCELED') return;
        setError(e?.response?.data?.detail || 'Could not scan the route for infrastructure.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, requestKey, nonce]);

  return {
    enabled,
    setEnabled,
    radiusM,
    setRadiusM,
    report,
    loading,
    error,
    stale: !!report && fetchedKey !== requestKey,
    capturedAt,
    refresh,
  };
}
